import React, { useMemo, useState } from 'react';
import propTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { Download, Filter, Search } from 'lucide-react';
import Button from 'tailwind-components/Button/Button';
import useMobile from 'hooks/useMobile';
import { cn } from 'lib/utils';
import { getAgentPositionOptions } from 'helpers/agencyManagement/agent-filters';
import FilterFields from './FilterFields';
import {
  SEARCH_PLACEHOLDER,
  fieldLabelClass,
  pillSearchSx
} from './filterFieldStyles';

/**
 * search / position / sort toolbar for the agency dashboard agents tables
 * @param agents - rows the Position dropdown builds its options from
 * @param searchQuery - current search text
 * @param onSearchChange - called with the new search text
 * @param positionFilter - currently selected position, or AGENT_FILTER_ALL
 * @param onPositionFilterChange - called with the newly selected position
 * @param sortOption - currently selected sort option value
 * @param onSortChange - called with the newly selected sort option value
 * @param onExportAgents - optional, renders the export button when provided
 * @param exportDisabled - disables the export button
 * @param positionAccessor - optional reader for rows that don't use the default agent shape
 */
export default function AgentsFiltersToolbar(props) {
  const {
    agents,
    searchQuery,
    onSearchChange,
    positionFilter,
    onPositionFilterChange,
    sortOption,
    onSortChange,
    onExportAgents,
    exportDisabled = false,
    positionAccessor
  } = props;

  const [filtersOpen, setFiltersOpen] = useState(false);
  const isMobile = useMobile();
  const showExport = typeof onExportAgents === 'function';

  const positionOptions = useMemo(
    () =>
      getAgentPositionOptions(
        agents,
        positionAccessor ? { position: positionAccessor } : undefined
      ),
    [agents, positionAccessor]
  );

  return (
    <div data-tour='agency-filters' className='w-full min-w-0'>
      {!isMobile && (
        <div className='flex w-full flex-col gap-3 min-[900px]:flex-row min-[900px]:items-center min-[900px]:justify-between min-[900px]:gap-6'>
          <div className='min-w-0 flex-1'>
            <FilterFields
              omitSearch={false}
              stacked={false}
              searchQuery={searchQuery}
              onSearchChange={onSearchChange}
              positionFilter={positionFilter}
              onPositionFilterChange={onPositionFilterChange}
              sortOption={sortOption}
              onSortChange={onSortChange}
              positionOptions={positionOptions}
            />
          </div>
          {showExport && (
            <div className='flex shrink-0 items-center gap-3'>
              <Button
                variant='primary'
                size='xsmall'
                icon={<Download />}
                text='Export All Agents'
                disabled={exportDisabled}
                onClick={onExportAgents}
              />
              <span className='shrink-0 text-xs leading-none text-grey-light whitespace-nowrap'>
                Updates every hour
              </span>
            </div>
          )}
        </div>
      )}

      {isMobile && (
        <div className='flex flex-col gap-3'>
          <div className='flex items-end gap-2'>
            <div className='min-w-0 flex-1 flex flex-col'>
              <span className={fieldLabelClass}>Search agents</span>
              <TextField
                size='small'
                placeholder={SEARCH_PLACEHOLDER}
                value={searchQuery}
                onChange={e => onSearchChange(e.target.value)}
                fullWidth
                sx={{
                  ...pillSearchSx,
                  maxWidth: { xs: '100%' }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start' sx={{ mr: 0 }}>
                      <Search className='size-[16px] text-grey' aria-hidden />
                    </InputAdornment>
                  )
                }}
                inputProps={{ 'aria-label': 'Search agents' }}
              />
            </div>
            <div className='relative shrink-0'>
              <Button
                type='button'
                variant={filtersOpen ? 'primaryLight' : 'primaryDarkened'}
                icon={<Filter size={20} />}
                onClick={e => {
                  e.stopPropagation();
                  setFiltersOpen(o => !o);
                }}
                aria-expanded={filtersOpen}
                aria-label='Open filters'
              />
              {filtersOpen && (
                <div
                  className={cn(
                    'absolute right-0 top-[calc(100%+8px)] w-[min(100vw-2rem,340px)]',
                    'rounded-xl border border-grey-lighter bg-white shadow-[0_8px_24px_rgba(0,0,0,0.12)]',
                    'overflow-y-auto max-h-[min(72vh,480px)] p-4'
                  )}
                  style={{ zIndex: 1100 }}
                >
                  <FilterFields
                    omitSearch
                    stacked
                    searchQuery={searchQuery}
                    onSearchChange={onSearchChange}
                    positionFilter={positionFilter}
                    onPositionFilterChange={onPositionFilterChange}
                    sortOption={sortOption}
                    onSortChange={onSortChange}
                    positionOptions={positionOptions}
                  />
                </div>
              )}
            </div>
          </div>
          {showExport && (
            <div className='flex items-center justify-between gap-2'>
              <Button
                variant='primary'
                size='xsmall'
                icon={<Download />}
                text='Export All Agents'
                disabled={exportDisabled}
                onClick={onExportAgents}
              />
              <p className='m-0 text-right text-xs text-grey-light'>
                Updates every hour
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

AgentsFiltersToolbar.propTypes = {
  agents: propTypes.arrayOf(propTypes.object).isRequired,
  searchQuery: propTypes.string.isRequired,
  onSearchChange: propTypes.func.isRequired,
  positionFilter: propTypes.string.isRequired,
  onPositionFilterChange: propTypes.func.isRequired,
  sortOption: propTypes.string.isRequired,
  onSortChange: propTypes.func.isRequired,
  onExportAgents: propTypes.func,
  exportDisabled: propTypes.bool,
  positionAccessor: propTypes.func
};
